import moment from 'moment';
import React, { ReactNode } from 'react';
import { FormattedMessage, IntlProvider } from 'react-intl';
import styled from 'styled-components';
import { ChangePlanEstimateResponse, Subscription } from 'api/billing/types';
import {
  getCurrentBillingCycle,
  getPlanPrice,
} from 'components/organisms/module-settings/billing/utils';
import translations from 'translations/en.json';

const Description = styled.span`
  font-size: var(--font-size-sm);
  color: var(--gray-80);
`;

const LicenseTitle = styled.p`
  color: var(--gray-70);
  margin-bottom: var(--spacing-02) !important;
`;

const Splitup = styled.p`
  color: var(--gray-50);
  margin-top: var(--spacing-02) !important;
`;

const EstimateDetails = styled.div`
  display: flex;
  align-items: end;
  justify-content: space-between;
  background: var(--green-10);
  border-left: 4px solid var(--green-50);
  padding: var(--spacing-04);
  margin-top: var(--spacing-03) !important;
  font-size: var(--font-size-sm);

  p {
    margin: 0;
  }

  .bold-figure {
    font-weight: var(--font-weight-bold);
    font-size: var(--font-size-base);

    &.invite-count {
      font-size: var(--heading-6-size);
      margin-bottom: var(--spacing-01);
    }
  }

  .billing-cycle {
    color: var(--gray-70);
  }
`;

export const getEstimateModalDescription = ({
  estimate,
  subscription,
  availableLicenses,
  inviteCount,
}: {
  estimate: ChangePlanEstimateResponse;
  subscription?: Subscription;
  availableLicenses: number;
  inviteCount: number;
}): ReactNode => {
  const additionalCount = inviteCount - availableLicenses;
  const planPrice = subscription
    ? getPlanPrice(subscription?.plan.planName)
    : 0;

  return (
    <IntlProvider locale="en" messages={translations}>
      <Description>
        <FormattedMessage
          id="billing.invite.members.estimate.modal.desc"
          values={{
            b: (chunks) => <b>{chunks}</b>,
            inviteCount,
            availableCount: availableLicenses,
            excessCount: additionalCount,
            startDate: moment().format('DD MMM'),
            endDate: moment(subscription?.endDate).format('DD MMM YYYY'),
          }}
        />
      </Description>
      <EstimateDetails>
        <div>
          <LicenseTitle>
            <FormattedMessage id="billing.invite.members.new.license" />
          </LicenseTitle>
          <p className="bold-figure invite-count">{additionalCount}</p>
          {planPrice && planPrice > 0 && (
            <Splitup>
              <FormattedMessage
                id="billing.estimate.splitup"
                values={{
                  inviteCount: additionalCount,
                  currencyCode: subscription?.currency,
                  price: planPrice,
                  startDate: moment().format('MMM DD'),
                  endDate: moment(subscription?.endDate).format("MMM DD'YY"),
                }}
              />
            </Splitup>
          )}
        </div>
        <div>
          <p className="bold-figure">{`${
            estimate?.amountDue ? estimate.amountDue / 100 : 0
          } ${estimate.currencyCode}`}</p>
          <p className="billing-cycle">
            <FormattedMessage
              id="billing.invite.members.billing.cycle"
              values={{
                billingCycle: subscription
                  ? getCurrentBillingCycle(subscription?.plan.planName).replace(
                      '-',
                      '_',
                    )
                  : '',
              }}
            />
          </p>
        </div>
      </EstimateDetails>
    </IntlProvider>
  );
};
